import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Store = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const selectedCategory = searchParams.get('category') || '';
  const sortBy = searchParams.get('sort') || 'featured';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products?`;
        if (selectedCategory) url += `category=${selectedCategory}&`;
        if (searchTerm) url += `search=${searchTerm}&`;
        
        const res = await axios.get(url);
        let data = res.data;
        
        // Sort products
        if (sortBy === 'price-low') {
          data.sort((a, b) => a.price - b.price);
        } else if (sortBy === 'price-high') {
          data.sort((a, b) => b.price - a.price);
        } else if (sortBy === 'featured') {
          data.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        }
        
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchProducts, 300);
    return () => clearTimeout(debounce);
  }, [selectedCategory, searchTerm, sortBy]);

  const handleCategoryChange = (category) => {
    if (category) {
      searchParams.set('category', category);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
    setMobileFilterOpen(false);
  };

  const handleSortChange = (sort) => {
    searchParams.set('sort', sort);
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchTerm('');
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? cat.name : 'All Products';
  };

  const FilterSidebar = ({ mobile = false }) => (
    <div className={mobile ? '' : 'hidden lg:block w-64 flex-shrink-0'}>
      <div className="sticky top-28">
        <h3 className="font-serif text-xl font-semibold mb-6">Categories</h3>
        <ul className="space-y-3">
          <li>
            <button
              onClick={() => handleCategoryChange('')}
              className={`text-left w-full py-2 text-sm transition-colors ${
                !selectedCategory ? 'text-[#0A0A0A] font-medium' : 'text-[#737373] hover:text-[#0A0A0A]'
              }`}
              data-testid="filter-all"
            >
              All Products
            </button>
          </li>
          {categories.map((category) => (
            <li key={category.id}>
              <button
                onClick={() => handleCategoryChange(category.slug)}
                className={`text-left w-full py-2 text-sm transition-colors ${
                  selectedCategory === category.slug 
                    ? 'text-[#0A0A0A] font-medium' 
                    : 'text-[#737373] hover:text-[#0A0A0A]'
                }`}
                data-testid={`filter-${category.slug}`}
              >
                {category.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Generate SEO title and description based on filters
  const seoTitle = selectedCategory 
    ? `${getCategoryName(selectedCategory)} - Shop Premium Products` 
    : 'Shop All Products - Premium Corporate Branding';
  
  const seoDescription = selectedCategory 
    ? `Browse our collection of premium ${getCategoryName(selectedCategory).toLowerCase()}. Quality craftsmanship for distinguished businesses.`
    : 'Discover our curated selection of premium branding products. Business cards, executive pens, leather diaries, and corporate apparel designed to elevate your corporate identity.';

  return (
    <div className="min-h-screen pt-20" data-testid="store-page">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        url={`/store${selectedCategory ? `?category=${selectedCategory}` : ''}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Store', url: '/store' },
          ...(selectedCategory ? [{ name: getCategoryName(selectedCategory) }] : [])
        ]}
      />
      {/* Header */}
      <div className="bg-[#FAFAFA] py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-[#0A0A0A]">
            {selectedCategory ? getCategoryName(selectedCategory) : 'Our Collection'}
          </h1>
          <p className="mt-4 text-[#737373] max-w-xl">
            Discover our curated selection of premium branding products designed to elevate your corporate identity.
          </p>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 rounded-none border-[#E5E5E5] focus:ring-[#0A0A0A]"
              data-testid="search-input"
            />
          </div>

          {/* Mobile Filter Button */}
          <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" className="rounded-none h-12 gap-2" data-testid="mobile-filter-btn">
                <Filter className="w-4 h-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full sm:w-80">
              <SheetHeader>
                <SheetTitle className="font-serif text-xl">Filters</SheetTitle>
              </SheetHeader>
              <div className="mt-8">
                <FilterSidebar mobile />
              </div>
            </SheetContent>
          </Sheet>

          {/* Sort Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none h-12 gap-2 min-w-[160px]" data-testid="sort-dropdown">
                Sort by
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-48">
              <DropdownMenuItem 
                onClick={() => handleSortChange('featured')}
                className={sortBy === 'featured' ? 'bg-[#F5F5F5]' : ''}
              >
                Featured
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleSortChange('price-low')}
                className={sortBy === 'price-low' ? 'bg-[#F5F5F5]' : ''}
              >
                Price: Low to High
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleSortChange('price-high')}
                className={sortBy === 'price-high' ? 'bg-[#F5F5F5]' : ''}
              >
                Price: High to Low
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear Filters */}
          {(selectedCategory || searchTerm) && (
            <Button 
              variant="ghost" 
              onClick={clearFilters}
              className="rounded-none h-12 gap-2"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4" />
              Clear
            </Button>
          )}
        </div>

        {/* Active Filters */}
        {selectedCategory && (
          <div className="mb-6 flex items-center gap-2">
            <span className="text-sm text-[#737373]">Filtering by:</span>
            <button
              onClick={() => handleCategoryChange('')}
              className="inline-flex items-center gap-1 px-3 py-1 bg-[#0A0A0A] text-white text-sm"
              data-testid="active-filter"
            >
              {getCategoryName(selectedCategory)}
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-12">
          {/* Desktop Sidebar */}
          <FilterSidebar />

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-[#F5F5F5]"></div>
                    <div className="mt-4 h-4 bg-[#F5F5F5] w-3/4"></div>
                    <div className="mt-2 h-3 bg-[#F5F5F5] w-1/4"></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16" data-testid="no-products">
                <p className="text-[#737373] text-lg">No products found</p>
                <Button 
                  onClick={clearFilters}
                  className="mt-4 rounded-none"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#737373] mb-6">{products.length} products</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Store;
